import type { UserTraits } from "@segment/analytics-next";
import type {
  BaseTrackEventData,
  MakeStrict,
  RefineStrictTrackParams,
} from "@shared/segmentEvents/types/eventData";
import isWithinXMinutes from "@shared/utils/isWithinXMinutes";
import { useQueryClient } from "@tanstack/vue-query";
import type { CategoryDto } from "mws-catalog-ts-sdk";
import {
  type BidDetailsProduct,
  BidType,
  type Category,
  CategoryType,
  type Event,
  type EventDetails,
  type EventParticipant,
  type EventPreference,
  type EventsOverview,
  type EventsOverviewProduct,
  ImageOrigin,
  type Order,
  OrderItemType,
  type OrderPaymentDetails,
  type Price,
  type Product,
  type ProductDetails,
  type ProductPreference,
  SalesMethod,
} from "mws-ts-v2-sdk";

import useRedditPixel from "~/composables/useRedditPixel";
import { queries } from "~/queries";
import { useFollowedCategoriesQuery } from "~/queries/catalog/mePreferenceCategories";
import type { EnrichedOrderItem } from "~/types/EnrichedOrderItem";
import { getPriceInCurrency } from "~/utils/priceUtils";

import {
  type BidClickedType,
  type BidType as BidPlacedType,
  type EventFollowedType,
  type MediaType,
  newsletterFormSubmitted,
  type StartingPoint as OnboardingStartingPoint,
  type OrderPaidCategories,
  type PageViewed,
  type PayButtonClickedCategories,
  type ProductCategories,
  productViewed,
  type ProductViewedCategories,
  type SearchSuggestionClicked,
  type Step,
  accountCreationPopupClicked as trackAccountCreationPopupClicked,
  accountCreationPopupShown as trackAccountCreationPopupShown,
  accountCreationPopupSkipped as trackAccountCreationPopupSkipped,
  addNewCardClicked as trackAddNewCardClicked,
  announcementClicked as trackAnnouncementClicked,
  authenticatedByOneTap as trackAuthenticatedByOneTap,
  bidClicked as trackBidClicked,
  bidPlaced as trackBidPlaced,
  buyNow as trackBuyNow,
  callToActionBlockClicked as trackCallToActionBlockClicked,
  cardBlockClicked as trackCardBlockClicked,
  cardTokenizationFailed as trackCardTokenizationFailed,
  categoryFollowed as trackCategoryFollowed,
  categoryFromSearchClicked as trackCategoryFromSearchClicked,
  categoryUnfollowed as trackCategoryUnfollowed,
  categoryViewed as trackCategoryViewed,
  checkoutStarted as trackCheckoutStarted,
  clickedRecentlyViewedProduct as trackClickedRecentlyViewedProduct,
  confirmBidClicked as trackConfirmBidClicked,
  contentSearched as trackContentSearched,
  deleteCardClicked as trackDeleteCardClicked,
  emailVerified as trackEmailVerified,
  eventCardClicked as trackEventCardClicked,
  eventClicked as trackEventClicked,
  eventFollowed as trackEventFollowed,
  eventNameClicked as trackEventNameClicked,
  eventUnfollowed as trackEventUnfollowed,
  eventViewed as trackEventViewed,
  experimentViewed as trackExperimentViewed,
  favouritesOnboardingScreenCompleted as trackFavouritesOnboardingScreenCompleted,
  favouritesOnboardingSkipped as trackFavouritesOnboardingSkipped,
  favouritesOnboardingStarted as trackFavouritesOnboardingStarted,
  filterButtonClicked as trackFilterButtonClicked,
  filtersApplied as trackFiltersApplied,
  filtersCleared as trackFiltersCleared,
  framingPresetSelected as trackFramingPresetSelected,
  framingSessionStarted as trackFramingSessionStarted,
  framingUpsellClicked as trackFramingUpsellClicked,
  fullWidthContentClicked as trackFullWidthContentClicked,
  giveawayJoinButtonClicked as trackGiveawayJoinButtonClicked,
  image360Closed as trackImage360Closed,
  image360Opened as trackImage360Opened,
  leaguePressed as trackLeaguePressed,
  loginClicked as trackLoginClicked,
  loginFormViewed as trackLoginFormViewed,
  loginSucceeded as trackLoginSucceeded,
  logoPressed as trackLogoPressed,
  menuItemClicked as trackMenuItemClicked,
  navigationItemClicked as trackNavigationItemClicked,
  notificationClicked as trackNotificationClicked,
  notificationsMarkedAllAsRead as trackNotificationsMarkedAllAsRead,
  notificationsViewed as trackNotificationsViewed,
  nowLiveItemClicked as trackNowLiveItemClicked,
  nowLiveViewed as trackNowLiveViewed,
  orderCompleted as trackOrderCompleted,
  pageViewed as trackPageViewed,
  payButtonClicked as trackPayButtonClicked,
  preAuthCreditCardSelected as trackPreAuthCreditCardSelected,
  preferencesOnboardingModalOpened as trackPreferencesOnboardingModalOpened,
  priceFilterApplied as trackPriceFilterApplied,
  productCarouselOpened as trackProductCarouselOpened,
  productClicked as trackProductClicked,
  productFollowed as trackProductFollowed,
  productImagesCarrousel as trackProductImagesCaroursel,
  productTabViewed as trackProductTabViewed,
  productUnfollowed as trackProductUnfollowed,
  raiseMaxBidClicked as trackRaiseMaxBidClicked,
  recentSearchItemClicked as trackRecentSearchItemClicked,
  registrationFormViewed as trackRegistrationFormViewed,
  registrationSucceeded as trackRegistrationSucceeded,
  searchItemClicked as trackSearchItemClicked,
  searchModalOpened as trackSearchModalOpened,
  searchSuggestionClicked as trackSearchSuggestionClicked,
  signedUp as trackSignedUp,
  sortingApplied as trackSortingApplied,
  teamMenuItemClicked as trackTeamMenuItemClicked,
  topMenuItemClicked as trackTopMenuItemClicked,
  upsellScreenViewed as trackUpsellScreenViewed,
  verificationOnboardingStepViewed as trackVerificationOnboardingStepViewed,
  viewAllProductsClicked as trackViewAllProductsClicked,
} from "#analytics";
import { OnboardingEntryType } from "#imports";

type TrackingCategories = MakeStrict<
  ProductViewedCategories &
    PayButtonClickedCategories &
    OrderPaidCategories &
    ProductCategories
>;

const useTypewriterEvent = (options: {
  platformId: string;
  isLoggedIn: Ref<boolean>;
  followedTeamNames: Ref<string[]>;
}) => {
  const { deviceType } = useScreenSize();
  const nuxtApp = useNuxtApp();
  const { properties } = getAnalyticsHeaders(nuxtApp);
  const baseWebEventData = computed<BaseTrackEventData>(() => ({
    platform_id: options.platformId,
    channel: "web",
    logged_in: options.isLoggedIn.value,
    followed_team_names: options.followedTeamNames.value,
    viewport_size: deviceType.value,
  }));

  return <C extends (...args: any[]) => void>(typewriterTrackEvent: C) =>
    (
      props: RefineStrictTrackParams<Parameters<C>[0]>,
      options?: Parameters<C>[1],
    ) => {
      const payload = { ...baseWebEventData.value, ...props };
      if (options || Object.keys(properties).length > 0) {
        const eventOptions = {
          ...options,
          ...(Object.keys(properties).length > 0
            ? {
                context: {
                  ...options?.context,
                  ad: {
                    ...options?.ad,
                    ...(properties.ttclid
                      ? { callback: properties.ttclid }
                      : {}), // tiktok click id https://business-api.tiktok.com/portal/docs?id=1739584860883969
                  },
                  campaign: {
                    ...options?.campaign,
                    ...properties,
                  },
                },
              }
            : {}),
        };

        return typewriterTrackEvent(payload, eventOptions);
      }

      return typewriterTrackEvent(payload);
    };
};

export const useSegmentEvent = () => {
  const { $segment } = useNuxtApp();

  const identify = (data: UserTraits) => {
    $segment.browser.value.identify(data);
  };

  return { identify };
};

const getAnalyticsOnboardingStartingPoint = (
  onboardingEntryType?: OnboardingEntryType,
): OnboardingStartingPoint => {
  const startingPointMap: Record<OnboardingEntryType, OnboardingStartingPoint> =
    {
      [OnboardingEntryType.Bid]: "BIDDING",
      [OnboardingEntryType.Follow]: "FAVOURITES",
      [OnboardingEntryType.Login]: "LOGIN",
      [OnboardingEntryType.AccountCreationPopup]: "ACCOUNT_CREATION_POPUP",
      [OnboardingEntryType.AccountCreationSearch]: "ACCOUNT_CREATION_SEARCH",
      [OnboardingEntryType.ClubSso]: "CLUB_SSO",
      [OnboardingEntryType.OneTap]: "ONE_TAP",
      [OnboardingEntryType.Giveaway]: "GIVEAWAY",
    };

  return onboardingEntryType ? startingPointMap[onboardingEntryType] : "LOGIN";
};

/**
 * Provides utility functions for tracking user interactions and events for analytics purposes
 *
 */
const _useAnalytics = () => {
  const queryClient = useQueryClient();
  const platformStore = usePlatformStore();
  const auth = useAuth();
  const { $segment, $typesenseAnalyticsClient } = useNuxtApp();

  const route = useRoute();
  const { trackRedditEvent } = useRedditPixel();
  const platformConfiguration = platformStore.tryGetPlatformConfiguration;
  const platformId = platformConfiguration?.platformId || "unknown";

  const { data: followedCategories } = useFollowedCategoriesQuery();

  const isLoggedIn = computed(() => auth?.isLoggedIn.value ?? false);
  const { user, userId } = useUser();
  const userName = computed(() => user.value?.name);

  const followedTeamNames = computed(() => {
    if (!isLoggedIn.value) return [];

    if (!followedCategories.value) return [];

    return followedCategories.value.results
      .filter((c) => c.type === CategoryType.Team)
      .map((c) => c.name);
  });

  const trackStrictTypewriterEvent = useTypewriterEvent({
    followedTeamNames,
    isLoggedIn,
    platformId,
  });

  const { identify } = useSegmentEvent();

  const { activeCurrency } = useActiveCurrency();
  const activeCurrencyCode = computed(
    () => activeCurrency.value.isoCurrencyCode,
  );

  const featuredTeams = (
    event: EventDetails | Event | EventPreference | undefined,
  ): EventParticipant[] | undefined =>
    event?.participants.filter((p) => p.isFeatured) || undefined;

  const featuredTeamName = (
    event: EventDetails | Event | EventPreference | undefined,
  ): string | undefined => featuredTeams(event)?.[0]?.name ?? undefined;

  // determines if the team is home away
  const getEventSide = (
    event?: EventDetails | Event | EventPreference | undefined,
  ) => (featuredTeams(event)?.[0]?.isHome ? "Home" : "Away");

  const getOrderItemPrice = (orderItem: EnrichedOrderItem) => ({
    price: orderItem.price?.price || 0,
    currency: orderItem.price?.currency || "EUR",
  });

  const getOrderItemName = (orderItem: EnrichedOrderItem) => {
    // Only return the product name for SKU products. Not for Frames or other types
    if (orderItem.orderItemType === OrderItemType.Sku) {
      return orderItem.product?.name ?? "";
    }

    return orderItem.name ?? "";
  };

  const getVariants = ({
    event,
    labels,
  }: {
    event?: EventDetails | Event | EventPreference | undefined;
    labels: string[];
  }) => {
    const productLabelsName =
      labels.length > 0 ? `- ${labels.join(" - ")}` : "";

    return `${getEventSide(event)} ${productLabelsName}`;
  };

  // Important for ads campaigns in meta
  const getContentCategory = (rawCategories?: CategoryDto[] | Category[]) => {
    const categories = getMappedCategories(rawCategories ?? []);

    return {
      category:
        (platformStore.isFrameTheGame
          ? categories.athlete?.name
          : categories.team?.name) || "",
    };
  };

  const getTrackMediaType = (
    product: Product | EventsOverviewProduct | ProductPreference,
  ): MediaType | undefined => {
    if (!("media" in product)) {
      return undefined;
    }

    if (product.media?.[0]?.metadata?.origin === ImageOrigin.Mockup) {
      return "mockup";
    }

    if ("image360Url" in product && product.image360Url?.length) {
      return "360";
    }

    return "photos";
  };

  const addProductClick = async (options: {
    product: Product | EventsOverviewProduct | ProductPreference;
    labels: string[];
    salesMethod: SalesMethod;
    euroPrice: number;
    activeCurrencyPrice: number;
    source_path: string;
    eventData: Event | EventPreference | EventsOverview | undefined;
    source: string;
    eventListIndex?: number;
  }) => {
    const {
      product,
      labels,
      salesMethod,
      euroPrice,
      activeCurrencyPrice,
      eventData,
    } = options;

    const event =
      !eventData || "categories" in eventData
        ? eventData
        : // Fetch the event categories if needed
          await queryClient.fetchQuery(
            queries.events.details(eventData.slug, platformId),
          );

    const sport = event?.categories?.find(
      (c) => c.externalReference?.type === CategoryType.Sport,
    );
    const country = event?.categories?.find(
      (c) => c.externalReference?.type === CategoryType.Country,
    );
    const league = event?.categories?.find(
      (c) => c.externalReference?.type === CategoryType.League,
    );

    trackStrictTypewriterEvent(trackProductClicked)({
      product_id: product.id,
      product_name: product.name || "",
      partner_id: (product as Product).partnerId || undefined,
      price_euro: euroPrice,
      price_euro_cents: euroPrice * 100,
      price: activeCurrencyPrice,
      price_cents: activeCurrencyPrice * 100,
      product_labels: labels?.join(", ") ?? "",
      product_sales_method: salesMethod,
      product_slug: product.slug!,
      product_sorting_priority:
        (product as Product).sortingPriority || undefined,
      currency: activeCurrencyCode.value,
      event_id: event?.id,
      event_name: event?.name ?? undefined,
      event_end_date: event?.endDate ?? undefined,
      event_start_date: event?.startDate ?? undefined,
      event_featured_team_name: featuredTeamName(event),
      sport_category_id: sport?.id,
      sport_category_name: sport?.name,
      country_category_id: country?.id,
      country_category_name: country?.name,
      league_category_id: league?.id,
      league_category_name: league?.name,
      list_id: event?.id ?? "",
      list_name: event?.name ?? "",
      highlights: product.highlights.map((highlight) => highlight.name),
      variant: getVariants({ event, labels }),
      event_list_position:
        options.eventListIndex === undefined
          ? undefined
          : options.eventListIndex + 1,
      source_path: options.source_path,
      source: options.source,
      media_type: getTrackMediaType(product),
    });
  };

  const addRecentlyViewedProductClick = async (options: {
    productPrices: Price[];
    productId: string;
    productName: string;
    productSlug: string;
    labels: string[];
    event?: EventDetails;
  }) => {
    const {
      productPrices,
      productId,
      productName,
      productSlug,
      labels,
      event,
    } = options;

    const activeCurrencyPrice = productPrices.find(
      (price) => price.currency === activeCurrencyCode.value,
    )?.price;

    trackStrictTypewriterEvent(trackClickedRecentlyViewedProduct)({
      currency: activeCurrencyCode.value,
      price: activeCurrencyPrice ?? 0,
      product_id: productId,
      product_name: productName,
      url: `${window.location.origin}/product/${productSlug}`,
      variant: getVariants({ event, labels }),
    });
  };

  const getMappedCategories = (
    categories: CategoryDto[] | Category[],
  ): TrackingCategories => {
    const getBasicCategoryOfType = (type: CategoryType) => {
      const category = categories.find(
        (c) => c.externalReference?.type === type,
      );

      return (
        category && {
          id: category.id,
          name: category.name,
        }
      );
    };

    return {
      athlete: getBasicCategoryOfType(CategoryType.Athlete),
      team: getBasicCategoryOfType(CategoryType.Team),
      league: getBasicCategoryOfType(CategoryType.League),
      country: getBasicCategoryOfType(CategoryType.Country),
      sport: getBasicCategoryOfType(CategoryType.Sport),
      other: getBasicCategoryOfType(CategoryType.Other),
    };
  };

  const addProductPageVisit = (product: ProductDetails | undefined) => {
    if (!product) return;

    const priceInEuro = getPriceInCurrency(product.prices, "EUR");
    const categories = getMappedCategories(product.categories);
    const teamCategory = categories.team?.name;

    trackStrictTypewriterEvent(productViewed)({
      ...getContentCategory(product.categories),
      product_id: categories.team?.id ?? product.id,
      product_name: product.name || "",
      currency: "EUR",
      price: priceInEuro,
      categories,
      // Data Required for meta
      name: product.name || "",
      content_type: "product",
      contents: [
        {
          id: categories.team?.id ?? product.id,
          name: product.name,
          value: priceInEuro,
          currency: "EUR",
          quantity: 1,
        },
      ],
      value: priceInEuro,
    });

    trackRedditEvent("ViewContent", {
      products: [
        {
          id: categories.team?.id || "", // we send the team id here for retargeting purposes
          category: teamCategory,
          name: product.name || "",
        },
      ],
    });
  };

  const constructCommonBidClickedTrackData = (
    product: BidDetailsProduct | ProductDetails,
    event: EventDetails,
    increment: number,
  ) => {
    const team = event.categories?.find(
      (c) => c.externalReference?.type === CategoryType.Team,
    );

    return {
      product_id: product.id,
      product_name: product.name,
      partner_id: product.partnerId,
      event_name: event.name,
      event_id: event.id,
      team_id: team?.id,
      event_featured_team_name: featuredTeamName(event),
      product_price_euro: getPriceInCurrency(product.prices, "EUR"),
      bid_increment: increment,
      value: increment,
      currency: activeCurrencyCode.value,
      product_price: getPriceInCurrency(
        product.prices,
        activeCurrencyCode.value,
      ),
      product_price_euro_cents: getPriceInCurrency(product.prices, "EUR") * 100,
      bid_increment_cents: increment * 100,
      product_price_cents:
        getPriceInCurrency(product.prices, activeCurrencyCode.value) * 100,
      auction_end_date: event.endDate,
      segment_spec_name: "Product Added" as const,
    };
  };

  const redditBidClickedData = (
    product: BidDetailsProduct | ProductDetails,
    event: EventDetails,
    type: BidClickedType,
  ) => {
    const team = event.categories?.find(
      (c) => c.externalReference?.type === CategoryType.Team,
    );

    return {
      itemCount: 1,
      products: [
        {
          id: team?.id ?? product.id,
          name: product.name || "",
        },
      ],
      type,
    };
  };

  const bidClicked = (
    product: BidDetailsProduct | ProductDetails | undefined,
    event: EventDetails | undefined,
    increment: number,
    amount: number,
    number_active_cards: number = 0,
    reservation_required: boolean = false,
    bidType: BidType,
  ) => {
    if (typeof product === "undefined" || typeof event === "undefined") return;

    const bidClickedType = bidType === BidType.Quickbid ? "QUICKBID" : "BID";

    trackRedditEvent(
      "AddToCart",
      redditBidClickedData(product, event, bidClickedType),
    );

    trackStrictTypewriterEvent(trackBidClicked)({
      ...constructCommonBidClickedTrackData(product, event, increment),
      type: bidClickedType,
      number_active_cards: number_active_cards,
      reservation_required: reservation_required,
      max_bid_amount: amount,
      max_bid_amount_cents: amount * 100,
      bid_type: bidType === BidType.Quickbid ? "quick" : "normal",
    });
  };

  const raiseMaxBidClicked = (
    product: BidDetailsProduct | ProductDetails | undefined,
    event: EventDetails | undefined,
    increment: number,
    amount: number,
    oldAmount: number,
  ) => {
    if (typeof product === "undefined" || typeof event === "undefined") return;

    trackStrictTypewriterEvent(trackRaiseMaxBidClicked)({
      ...constructCommonBidClickedTrackData(product, event, increment),
      max_bid_amount: amount,
      max_bid_amount_cents: amount * 100,
      old_max_bid_amount: oldAmount,
      old_max_bid_amount_cents: oldAmount * 100,
    });
  };

  const constructCommonBidPlacedTrackData = (
    product: BidDetailsProduct | ProductDetails,
    event: EventDetails | Event | undefined,
    bidId: string,
    bidAmount: number,
    isOutbid: boolean,
    increment: number,
    bidType: BidType,
  ) => {
    const labels = product?.labels.map((label) => label.text);

    const relatedCategories =
      "categories" in product ? product.categories : event?.categories;

    const mappedCategories = relatedCategories
      ? getMappedCategories(relatedCategories)
      : {};

    const bidPlacedType: BidPlacedType =
      bidType === BidType.Quickbid ? "quick" : "normal";

    return {
      ...getContentCategory(event?.categories),
      marketplace_event_id: event?.id,
      product_id: product.id,
      product_name: product.name,
      partner_id: product.partnerId,
      team_id: mappedCategories.team?.id,
      bid_amount: bidAmount,
      bid_increment: increment,
      bid_type: bidPlacedType,
      product_labels:
        product.labels?.map((label) => label.text).join(", ") ?? "",
      product_slug: product.slug,
      product_sorting_priority: product.sortingPriority,
      currency: "EUR", // Required for meta hard coded to match currency of bid increment
      event_id: bidId,
      marketplace_event_name: event?.name ?? undefined,
      event_end_date: event?.endDate ?? undefined,
      event_start_date: event?.startDate ?? undefined,
      event_featured_team_name: featuredTeamName(event),
      sport_category_id: mappedCategories.sport?.id,
      sport_category_name: mappedCategories.sport?.name,
      country_category_id: mappedCategories.country?.id,
      country_category_name: mappedCategories.country?.name,
      league_category_id: mappedCategories.league?.id,
      league_category_name: mappedCategories.league?.name,
      bid_id: bidId,
      variant: getVariants({ event, labels }),
      categories: mappedCategories,

      // Required for GA4
      items: [
        {
          index: 0,
          item_id: product.id,
          item_name: product.name,
          price: increment,
          quantity: 1,
          currency: "EUR",
          item_brand: featuredTeamName(event) || "",
          item_category: mappedCategories.sport?.name || "",
          item_category2: mappedCategories.country?.name || "",
          item_category3: mappedCategories.league?.name || "",
          item_category4: event?.name || "",
          item_list_id: event?.id || "",
          item_list_name: event?.name || "",
        },
      ],

      // Data Required for meta
      name: product.name,
      content_type: "product" as const,
      contents: [
        {
          id: mappedCategories.team?.id ?? product.id,
          name: product.name,
          value: increment ?? bidAmount,
          currency: "EUR",
          quantity: 1,
        },
      ],
      value: increment,
      is_outbid: isOutbid,
    };
  };

  const bidPlaced = (
    product: BidDetailsProduct | ProductDetails | undefined,
    event: EventDetails | Event | undefined,
    bidId: string,
    bidAmount: number,
    bidIncrement: number,
    isOutbid: boolean,
    bidType: BidType,
  ) => {
    if (typeof product === "undefined") return;

    const bidPlacedData = constructCommonBidPlacedTrackData(
      product,
      event,
      bidId,
      bidAmount,
      isOutbid,
      bidIncrement,
      bidType,
    );
    trackStrictTypewriterEvent(trackBidPlaced)(bidPlacedData);

    trackRedditEvent("Purchase", {
      itemCount: 1,
      products: [
        {
          id: bidPlacedData.categories.team?.id,
          category: bidPlacedData.categories.team?.name,
          name: product.name || "",
        },
      ],
      value: bidIncrement,
      currency: "EUR",
      conversionId: bidId,
    });
  };

  const emailVerified = trackStrictTypewriterEvent(trackEmailVerified);

  const signedUp = ({ event_id }: { event_id: string | undefined }) => {
    trackStrictTypewriterEvent(trackSignedUp)({ event_id });

    trackRedditEvent("SignUp", {
      conversionId: event_id,
    });
  };

  const loginFormViewed = (onboardingEntryType?: OnboardingEntryType) => {
    trackStrictTypewriterEvent(trackLoginFormViewed)({
      onboarding_starting_point:
        getAnalyticsOnboardingStartingPoint(onboardingEntryType),
    });
  };

  const loginClicked = trackStrictTypewriterEvent(trackLoginClicked);

  const registrationFormViewed = ({
    entry,
  }: {
    entry: OnboardingEntryType;
  }) => {
    trackStrictTypewriterEvent(trackRegistrationFormViewed)({
      starting_point: getAnalyticsOnboardingStartingPoint(entry),
      slug: route.path,
    });
  };

  const loginSucceeded = async (onboardingEntryType?: OnboardingEntryType) => {
    if (!isLoggedIn.value) return;

    const userDetails = await queryClient.ensureQueryData(queries.me.details());

    const REGISTRATION_WINDOW_MINUTES = 5 * 60 * 1000; // 5 minutes in milliseconds
    const isWithin5Minutes = isWithinXMinutes({
      date: userDetails.registeredAt,
      minutesMs: REGISTRATION_WINDOW_MINUTES,
    });

    if (isWithin5Minutes) {
      trackRedditEvent("SignUp", {
        conversionId: userDetails.id,
      });
    }

    trackStrictTypewriterEvent(
      isWithin5Minutes ? trackRegistrationSucceeded : trackLoginSucceeded,
    )({
      identity_providers: userDetails.identityProviders,
      onboarding_starting_point:
        getAnalyticsOnboardingStartingPoint(onboardingEntryType),
    });
  };

  const preferenceOnboardingModalOpened = trackStrictTypewriterEvent(
    trackPreferencesOnboardingModalOpened,
  );

  const favouritesOnboardingScreenCompleted = trackStrictTypewriterEvent(
    trackFavouritesOnboardingScreenCompleted,
  );

  const favouritesOnboardingSkipped = trackStrictTypewriterEvent(
    trackFavouritesOnboardingSkipped,
  );

  const favouritesOnboardingStarted = trackStrictTypewriterEvent(
    trackFavouritesOnboardingStarted,
  );

  const productFollowed = (
    type: "follow" | "unfollow",
    data: {
      type?: EventFollowedType;
      productId: string;
      productName: string;
      priceInEuros?: number;
      categories?: Category[];
    },
  ) => {
    const categories = getMappedCategories(data.categories || []);

    if (type === "follow") {
      trackRedditEvent("AddToWishlist", {
        itemCount: 1,
        products: [
          {
            id: data.productId,
            category: categories.team?.name,
            name: data.productName || "",
          },
        ],
        value: data.priceInEuros,
        currency: "EUR",
        conversionId: `${data.productId}_${userId.value}`,
      });

      return trackStrictTypewriterEvent(trackProductFollowed)({
        ...getContentCategory(data.categories),
        currency: "EUR",
        price: data.priceInEuros,
        product_id: data.productId,
        product_name: data.productName,
        type: data.type ? data.type : "default",
        sport_category: categories.sport?.name,
        sport_category_id: categories.sport?.id,
        categories,
        user_id: userId.value,
        event_id: `${data.productId}_${userId.value}`,
        // Data Required for meta
        content_name: data.productName,
        content_type: "product",
        value: data.priceInEuros,
        contents: [
          {
            id: data.productId,
            name: data.productName,
            value: data.priceInEuros,
            currency: "EUR",
            quantity: 1,
          },
        ],
      });
    }

    trackStrictTypewriterEvent(trackProductUnfollowed)({
      product_id: data.productId,
      type: data.type ? data.type : "default",
    });
  };

  const newsletterSubmitted = async (data: {
    email: string;
    categoryId: string;
  }) => {
    await Promise.all([
      identify({
        platform_id: platformId,
        email: data.email,
        newsletter_category_id: data.categoryId,
      }),

      trackStrictTypewriterEvent(newsletterFormSubmitted)({
        category_id: data.categoryId,
      }),
    ]);
  };

  const buyNow = (product: ProductDetails) => {
    const categories = getMappedCategories(product.categories);
    const playerName = categories.athlete?.name;

    trackStrictTypewriterEvent(trackBuyNow)({
      product_id: product.id,
      product_name: product.name,
      product_photo: product.media[0]?.url ?? "",
      player_name: playerName,
      user_name: userName.value,
      currency: "EUR",
      price: getPriceInCurrency(product.prices, "EUR"),
      quantity: 1,
    });
  };

  const framingSessionStarted = (
    product: ProductDetails,
    presetId: string | undefined,
  ) => {
    const categories = getMappedCategories(product.categories);
    const playerName = categories.athlete?.name;

    trackStrictTypewriterEvent(trackFramingSessionStarted)({
      product_id: product.id,
      product_name: product.name,
      product_photo: product.media[0]?.url ?? "",
      player_name: playerName,
      user_name: userName.value,
      price: getPriceInCurrency(product.prices, "EUR"),
      quantity: 1,
      preset_id: presetId,
    });
  };

  const framingPresetSelected = (data: {
    productId: string;
    presetId: string;
  }) => {
    trackStrictTypewriterEvent(trackFramingPresetSelected)({
      product_id: data.productId,
      preset_id: data.presetId,
    });
  };

  const framingUpsellClicked = trackStrictTypewriterEvent(
    trackFramingUpsellClicked,
  );

  const upsellScreenViewed = trackStrictTypewriterEvent(
    trackUpsellScreenViewed,
  );

  const giveawayJoinButtonClicked = ({
    giveawaySlug,
    giveawayMetaData,
  }: {
    giveawaySlug: string;
    giveawayMetaData: string;
  }) => {
    trackStrictTypewriterEvent(trackGiveawayJoinButtonClicked)({
      giveaway_slug: giveawaySlug,
      giveaway_meta_data: giveawayMetaData,
    });
  };

  const payButtonClicked = (
    order: OrderPaymentDetails,
    orderItemProducts: EnrichedOrderItem[],
    paymentType: string,
  ) => {
    const rawCategories = orderItemProducts[0]?.product?.categories;
    trackStrictTypewriterEvent(trackPayButtonClicked)({
      payment_type: paymentType,
      currency: order.itemsTotal?.currency ?? undefined,
      value: order.itemsTotal?.price ?? 0,
      items: orderItemProducts.map((item, index) => ({
        ...getOrderItemPrice(item),
        index,
        item_id: item.product?.id ?? "",
        quantity: item.quantity,
        item_name: getOrderItemName(item) ?? undefined,
        event_name: item.eventName ?? "",
        framing_type: item.product?.framingType,
      })),
      categories: rawCategories ? getMappedCategories(rawCategories) : {},
    });
  };

  const orderPaid = (order: Order, orderItemProducts: EnrichedOrderItem[]) => {
    trackStrictTypewriterEvent(trackOrderCompleted)({
      ...getContentCategory(orderItemProducts[0]?.product?.categories),
      name: orderItemProducts[0]?.name || "",
      order_id: order.id,
      event_id: order.id,
      currency: order.itemsPrice.currency || "EUR",
      total: order.totalPrice.price,
      revenue: order.totalPrice.price,
      tax: order.vat?.price,
      shipping: order.shippingCosts?.price,
      products: orderItemProducts.map((item, index) => ({
        ...getOrderItemPrice(item),
        product_id: item.product?.id ?? "",
        item_name: getOrderItemName(item) ?? undefined,
        event_name: item.eventName ?? "",
        price: item.price?.price,
        index,
        quantity: item.quantity,
        framing_type: item.product?.framingType,
        categories: item.product?.categories
          ? getMappedCategories(item.product?.categories)
          : {},
      })),
    });
  };

  const checkoutPaymentStepEntered = (
    order: OrderPaymentDetails,
    orderItemProducts: EnrichedOrderItem[],
    orderId: string,
  ) => {
    const checkoutStarted = trackStrictTypewriterEvent(trackCheckoutStarted);

    checkoutStarted({
      ...getContentCategory(orderItemProducts[0]?.product?.categories),
      currency: order.itemsTotal?.currency || "EUR",
      order_id: orderId,
      shipping: order.shippingCosts?.price,
      tax: order.vat?.price,
      revenue: order.totalPrice?.price,
      value: order.totalPrice?.price,
      products: orderItemProducts.map((item, index) => ({
        ...getOrderItemPrice(item),
        product_id: item.product?.id ?? "",
        name: getOrderItemName(item),
        quantity: item.quantity,
        framing_type: item.product?.framingType,
        position: index,
        categories: item.product?.categories
          ? getMappedCategories(item.product?.categories)
          : {},
      })),
    });
  };

  const filterButtonClicked = trackStrictTypewriterEvent(
    trackFilterButtonClicked,
  );
  const filtersApplied = trackStrictTypewriterEvent(trackFiltersApplied);
  const filtersCleared = trackStrictTypewriterEvent(trackFiltersCleared);
  const sortingApplied = trackStrictTypewriterEvent(trackSortingApplied);
  const priceFilterApplied = trackStrictTypewriterEvent(
    trackPriceFilterApplied,
  );
  const topMenuItemClicked = trackStrictTypewriterEvent(
    trackTopMenuItemClicked,
  );

  const menuItemClicked = trackStrictTypewriterEvent(trackMenuItemClicked);

  const nowLiveItemClicked = trackStrictTypewriterEvent(
    trackNowLiveItemClicked,
  );

  const navigationItemClicked = trackStrictTypewriterEvent(
    (params: {
      sectionIndex: number;
      menuIndex: number;
      menuSection: string;
      linkName: string;
      menuName: string;
    }) => {
      const position = `${params.sectionIndex + 1}, ${params.menuIndex + 1}`;
      trackNavigationItemClicked({
        position: position,
        menu_section: params.menuSection,
        link_name: params.linkName,
        menu: params.menuName,
      });
    },
  );

  const teamMenuItemClicked = trackStrictTypewriterEvent(
    trackTeamMenuItemClicked,
  );

  const nowLiveViewed = trackStrictTypewriterEvent(trackNowLiveViewed);

  const searchSuggestionClicked = trackStrictTypewriterEvent(
    (params: { suggestion_category_id: string } & SearchSuggestionClicked) => {
      trackSearchSuggestionClicked({
        category_type: params.category_type,
        search_suggestion: params.search_suggestion,
        search_term: params.search_term,
        suggestion_category_id: params.suggestion_category_id,
      });

      // Also track to typesense analytics
      $segment.browser.value.user().then((analyticsUser) => {
        $typesenseAnalyticsClient?.events().create({
          type: "click",
          name: "category_hit_event",
          data: {
            doc_id: params.suggestion_category_id,
            user_id:
              userId.value || analyticsUser?.anonymousId() || "anonymous",
          },
        });
      });
    },
  );
  const searchModalOpened = trackStrictTypewriterEvent(trackSearchModalOpened);
  const contentSearched = trackStrictTypewriterEvent(trackContentSearched);
  const recentSearchItemClicked = trackStrictTypewriterEvent(
    trackRecentSearchItemClicked,
  );
  const searchItemClicked = trackStrictTypewriterEvent(trackSearchItemClicked);
  const categoryFromSearchClicked = trackStrictTypewriterEvent(
    trackCategoryFromSearchClicked,
  );

  const pageViewed = (
    props: Omit<PageViewed, "channel" | "logged_in" | "platform_id">,
  ) => {
    trackStrictTypewriterEvent(trackPageViewed)(props);
    trackRedditEvent("PageVisit");
  };

  const categoryFollowed = trackStrictTypewriterEvent(trackCategoryFollowed);
  const categoryUnfollowed = trackStrictTypewriterEvent(
    trackCategoryUnfollowed,
  );
  const eventFollowed = trackStrictTypewriterEvent(trackEventFollowed);
  const eventUnfollowed = trackStrictTypewriterEvent(trackEventUnfollowed);
  const eventCardClicked = trackStrictTypewriterEvent(trackEventCardClicked);
  const eventClicked = trackStrictTypewriterEvent(trackEventClicked);
  const cardBlockClicked = trackStrictTypewriterEvent(trackCardBlockClicked);
  const callToActionBlockClicked = trackStrictTypewriterEvent(
    trackCallToActionBlockClicked,
  );
  const announcementClicked = trackStrictTypewriterEvent(
    trackAnnouncementClicked,
  );

  const productTabViewed = trackStrictTypewriterEvent(trackProductTabViewed);

  const leaguePressed = trackStrictTypewriterEvent(trackLeaguePressed);

  const fullWidthContentClicked = trackStrictTypewriterEvent(
    trackFullWidthContentClicked,
  );

  const experimentViewed = trackStrictTypewriterEvent(trackExperimentViewed);

  const viewAllProductsClicked = trackStrictTypewriterEvent(
    trackViewAllProductsClicked,
  );
  const verificationOnboardingStepViewed = ({
    step,
    bidLimit,
    onboardingEntry,
  }: {
    step: Step;
    bidLimit?: number;
    onboardingEntry?: OnboardingEntry;
  }) => {
    if (onboardingEntry?.type !== OnboardingEntryType.Bid) return;

    const onboardingStepViewed = trackStrictTypewriterEvent(
      trackVerificationOnboardingStepViewed,
    );

    onboardingStepViewed({
      step,
      bid_amount: onboardingEntry?.data?.bidAmount ?? undefined,
      bid_amount_currency: activeCurrency.value.isoCurrencyCode,
      user_bid_limit: bidLimit ?? 0,
    });
  };

  const categoryViewed = (category: Category) => {
    trackStrictTypewriterEvent(trackCategoryViewed)({
      category_id: category.id,
      category_name: category.name,
      category_type: category.externalReference?.type ?? CategoryType.Other,
    });

    if (category.externalReference?.type === CategoryType.Team) {
      trackRedditEvent("ViewContent", {
        // we send the category as a product for retargeting purposes
        products: [
          {
            id: category.id,
            category: category.name,
            name: category.name,
          },
        ],
      });
    }
  };

  const eventViewed = (event: EventDetails) => {
    const categories = getMappedCategories(event.categories);

    trackStrictTypewriterEvent(trackEventViewed)({
      event_id: event.id,
      event_name: event.name,
      end_date: event.endDate,
      categories: getMappedCategories(event.categories),
    });

    trackRedditEvent("ViewContent", {
      // we send the category as a product for retargeting purposes
      products: [
        {
          id: categories.team?.id,
          category: categories.team?.name || "",
          name: categories.team?.name || "",
        },
      ],
    });
  };

  const productCarouselChanged = trackStrictTypewriterEvent(
    trackProductImagesCaroursel,
  );
  const productCarouselOpened = trackStrictTypewriterEvent(
    trackProductCarouselOpened,
  );

  const eventNameClicked = trackStrictTypewriterEvent(trackEventNameClicked);
  const logoPressed = trackStrictTypewriterEvent(trackLogoPressed);

  const authenticatedByOneTap = trackStrictTypewriterEvent(
    trackAuthenticatedByOneTap,
  );

  const image360Opened = trackStrictTypewriterEvent(trackImage360Opened);
  const image360Closed = trackStrictTypewriterEvent(trackImage360Closed);

  return {
    // identity
    signedUp,
    emailVerified,
    loginFormViewed,
    loginClicked,
    loginSucceeded,
    registrationFormViewed,
    authenticatedByOneTap,

    // ecommerce content
    addProductPageVisit,
    addProductClick,

    // normal bidding
    bidClicked,
    bidPlaced,

    // max bidding
    raiseMaxBidClicked,

    // filtering & sorting
    filterButtonClicked,
    filtersApplied,
    filtersCleared,
    sortingApplied,
    priceFilterApplied,

    // user engagement
    giveawayJoinButtonClicked,
    newsletterSubmitted,
    preferenceOnboardingModalOpened,
    categoryFollowed,
    categoryUnfollowed,
    eventFollowed,
    eventUnfollowed,
    eventCardClicked,
    eventClicked,
    cardBlockClicked,
    productFollowed,
    callToActionBlockClicked,
    announcementClicked,
    searchItemClicked,
    topMenuItemClicked,
    menuItemClicked,
    nowLiveViewed,
    nowLiveItemClicked,
    navigationItemClicked,
    teamMenuItemClicked,
    leaguePressed,
    addRecentlyViewedProductClick,
    viewAllProductsClicked,
    categoryViewed,
    eventViewed,
    eventNameClicked,
    logoPressed,

    // FTG
    framingPresetSelected,
    framingSessionStarted,
    framingUpsellClicked,
    upsellScreenViewed,
    buyNow,

    // orders
    payButtonClicked,
    orderPaid,
    checkoutPaymentStepEntered,

    // search
    contentSearched,
    searchModalOpened,
    searchSuggestionClicked,
    recentSearchItemClicked,
    categoryFromSearchClicked,

    // global
    pageViewed,

    // product
    productTabViewed,
    productCarouselChanged,
    productCarouselOpened,

    // 360 images
    image360Opened,
    image360Closed,

    // blocks
    fullWidthContentClicked,

    // onboarding
    favouritesOnboardingScreenCompleted,
    favouritesOnboardingSkipped,
    favouritesOnboardingStarted,
    verificationOnboardingStepViewed,

    // a/b testing
    experimentViewed,

    // account creation popup
    accountCreationPopupClicked: trackStrictTypewriterEvent(
      trackAccountCreationPopupClicked,
    ),
    accountCreationPopupShown: trackStrictTypewriterEvent(
      trackAccountCreationPopupShown,
    ),
    accountCreationPopupSkipped: trackStrictTypewriterEvent(
      trackAccountCreationPopupSkipped,
    ),

    // card tokenization
    cardTokenizationFailed: trackStrictTypewriterEvent(
      trackCardTokenizationFailed,
    ),
    addNewCardClicked: trackStrictTypewriterEvent(trackAddNewCardClicked),
    deleteCardClicked: trackStrictTypewriterEvent(trackDeleteCardClicked),
    preAuthCreditCardSelected: trackStrictTypewriterEvent(
      trackPreAuthCreditCardSelected,
    ),
    confirmBidClicked: trackStrictTypewriterEvent(trackConfirmBidClicked),

    // notification centre
    notificationsViewed: trackStrictTypewriterEvent(trackNotificationsViewed),
    notificationClicked: trackStrictTypewriterEvent(trackNotificationClicked),
    notificationsMarkedAllAsRead: trackStrictTypewriterEvent(
      trackNotificationsMarkedAllAsRead,
    ),
  };
};

export default createSharedComposable(_useAnalytics);
